import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { CURRENCIES, fmt, fmtIDR } from "../lib/format";
import { Plus, Clock, TrendingUp, Wallet, Users, X, Loader2, DoorOpen, DoorClosed } from "lucide-react";
import { toast } from "sonner";

export default function OfficerHome() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [rates, setRates] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [closeForm, setCloseForm] = useState(false);

  const load = () => {
    api.get("/dashboard/officer").then((r) => setData(r.data)).catch(() => {});
    api.get("/rates").then((r) => setRates(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const shift = data?.shift;
  const today = data?.today;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="font-head text-2xl font-bold">POS Dashboard</h1>
      </div>

      {/* Shift status */}
      {!shift ? (
        <div className="nnx-card p-5 border-[rgba(234,179,8,0.35)]" data-testid="shift-closed-card">
          <div className="flex items-center gap-2 text-[#EAB308] mb-1"><DoorClosed className="h-5 w-5" /><span className="font-semibold">Shift Belum Dibuka</span></div>
          <p className="text-sm text-[#94A3B8] mb-4">Buka shift dulu dan masukkan modal awal sebelum mulai transaksi.</p>
          <button className="btn-gold" data-testid="open-shift-btn" onClick={() => setOpenForm(true)}>OPEN SHIFT</button>
        </div>
      ) : (
        <div className="nnx-card p-5" data-testid="shift-open-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 text-emerald-400 text-sm mb-1"><span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" /> SHIFT AKTIF</div>
              <div className="mono text-xl font-bold">{shift.shift_code}</div>
              <div className="text-xs text-[#64748B] mt-1 flex items-center gap-1"><Clock className="h-3 w-3" /> Buka: {new Date(shift.open_time).toLocaleString("id-ID")}</div>
            </div>
            <button className="btn-outline hover:border-rose-500/50 hover:text-rose-400" data-testid="close-shift-btn" onClick={() => setCloseForm(true)}><DoorOpen className="h-4 w-4 inline mr-1" /> CLOSE SHIFT</button>
          </div>
        </div>
      )}

      {/* Big new transaction */}
      <button data-testid="new-transaction-cta" disabled={!shift} onClick={() => nav("/new")}
        className="w-full rounded-2xl p-6 flex items-center justify-center gap-3 text-xl font-head font-bold text-[#0B0E14] transition-all active:scale-[0.98] disabled:opacity-40"
        style={{ background: "linear-gradient(135deg,#EAB308,#CA8A04)" }}>
        <Plus className="h-7 w-7" /> TRANSAKSI BARU
      </button>
      {!shift && <p className="text-xs text-[#64748B] text-center -mt-2">Buka shift dulu untuk mengaktifkan tombol transaksi.</p>}

      {/* Today stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={TrendingUp} label="Transaksi Hari Ini" value={today?.count ?? 0} testid="stat-count" />
        <Stat icon={Wallet} label="Volume (IDR)" value={fmtIDR(today?.volume)} testid="stat-volume" />
        <Stat icon={Users} label="Profit Kotor" value={fmtIDR(today?.gross_profit)} accent testid="stat-profit" />
        <Stat icon={Wallet} label="Net Profit" value={fmtIDR(today?.net_profit)} accent testid="stat-net" />
      </div>

      {/* Rate quick view */}
      <div className="nnx-card p-4">
        <div className="label mb-3">Current Rate</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {rates.map((r) => (
            <div key={r.currency} className="rounded-lg border border-[#222B3D] bg-[#0B0E14] p-3">
              <div className="text-sm font-semibold">{r.currency}</div>
              <div className="text-xs text-emerald-400 mono mt-1">Buy {Number(r.buy_rate).toLocaleString()}</div>
              <div className="text-xs text-rose-400 mono">Sell {Number(r.sell_rate).toLocaleString()}</div>
            </div>
          ))}
        </div>
      </div>

      {openForm && <OpenShiftModal onClose={() => setOpenForm(false)} onDone={() => { setOpenForm(false); load(); }} />}
      {closeForm && shift && <CloseShiftModal shift={shift} onClose={() => setCloseForm(false)} onDone={() => { setCloseForm(false); load(); }} />}
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="nnx-card p-4" data-testid={testid}>
      <Icon className={`h-4 w-4 mb-2 ${accent ? "text-[#EAB308]" : "text-[#64748B]"}`} />
      <div className="label">{label}</div>
      <div className={`mono font-bold text-lg mt-1 ${accent ? "text-[#EAB308]" : "text-white"}`}>{value}</div>
    </div>
  );
}

function OpenShiftModal({ onClose, onDone }) {
  const [bal, setBal] = useState(Object.fromEntries(CURRENCIES.map((c) => [c.code, ""])));
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/shifts/open", { opening_balances: Object.fromEntries(Object.entries(bal).map(([k, v]) => [k, v || "0"])) });
      toast.success("Shift dibuka");
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  return (
    <Modal title="Open Shift — Modal Awal" onClose={onClose} testid="open-shift-modal">
      <div className="grid grid-cols-2 gap-3">
        {CURRENCIES.map((c) => (
          <div key={c.code}>
            <label className="label">{c.code}</label>
            <input data-testid={`open-bal-${c.code}`} inputMode="decimal" className="nnx-input mt-1 mono" value={bal[c.code]} onChange={(e) => setBal({ ...bal, [c.code]: e.target.value })} placeholder="0" />
          </div>
        ))}
      </div>
      <button className="btn-gold w-full mt-4" data-testid="confirm-open-shift" onClick={submit} disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "BUKA SHIFT"}</button>
    </Modal>
  );
}

function CloseShiftModal({ shift, onClose, onDone }) {
  const [bal, setBal] = useState(Object.fromEntries(CURRENCIES.map((c) => [c.code, ""])));
  const [note, setNote] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/shifts/${shift.id}/close`, {
        actual_closing: Object.fromEntries(Object.entries(bal).map(([k, v]) => [k, v || "0"])),
        note,
      });
      setResult(r.data);
      toast.success("Shift ditutup");
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  if (result) {
    return (
      <Modal title="Shift Ditutup — Rekonsiliasi" onClose={onDone} testid="close-shift-result">
        <div className="space-y-2">
          {CURRENCIES.map((c) => {
            const diff = Number(result.differences[c.code]);
            return (
              <div key={c.code} className="flex items-center justify-between text-sm border-b border-[#222B3D] py-2">
                <span className="font-medium">{c.code}</span>
                <div className="flex gap-4 mono text-xs">
                  <span className="text-[#64748B]">Exp {Number(result.expected_closing[c.code]).toLocaleString()}</span>
                  <span>Act {Number(result.actual_closing[c.code]).toLocaleString()}</span>
                  <span className={diff === 0 ? "text-emerald-400" : "text-rose-400"}>{diff > 0 ? "+" : ""}{diff.toLocaleString()}</span>
                </div>
              </div>
            );
          })}
        </div>
        <button className="btn-gold w-full mt-4" onClick={onDone}>SELESAI</button>
      </Modal>
    );
  }
  return (
    <Modal title="Close Shift — Hitung Kas Aktual" onClose={onClose} testid="close-shift-modal">
      <div className="grid grid-cols-2 gap-3">
        {CURRENCIES.map((c) => (
          <div key={c.code}>
            <label className="label">{c.code} (aktual)</label>
            <input data-testid={`close-bal-${c.code}`} inputMode="decimal" className="nnx-input mt-1 mono" value={bal[c.code]} onChange={(e) => setBal({ ...bal, [c.code]: e.target.value })} placeholder="0" />
          </div>
        ))}
      </div>
      <textarea data-testid="close-note" className="nnx-input mt-3" rows={2} placeholder="Catatan (wajib jika ada selisih)" value={note} onChange={(e) => setNote(e.target.value)} />
      <button className="btn-gold w-full mt-4" data-testid="confirm-close-shift" onClick={submit} disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "TUTUP SHIFT"}</button>
    </Modal>
  );
}

export function Modal({ title, children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div data-testid={testid} className="nnx-card w-full sm:max-w-lg p-5 rounded-t-2xl sm:rounded-xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-head font-bold text-lg">{title}</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-[#222B3D]"><X className="h-5 w-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
